import { apiSlice } from "../../../features/api/apiSlice";

/**
 * The national positions register — past presidents and secretaries, the Board
 * of Trustees, the national bodies and the Institute's external representatives.
 *
 * Every mutation invalidates the one list: appointing somebody changes both the
 * holder rows and the counts the page renders off them.
 */

export type NationalGroupKey =
  | "past_president"
  | "past_secretary"
  | "bot"
  | "national_body"
  | "qsrbn_rep"
  | "other";

export type NationalHolder = {
  id: number;
  national_position_id: number;
  member_id: string;
  title: string | null;
  started_at: string | null;
  ended_at: string | null;
  is_current: boolean;
  member?: {
    id: string;
    membership_no?: string | null;
    member_member_no?: string | null;
    title?: string | null;
    surname?: string | null;
    firstname?: string | null;
    othernames?: string | null;
    email?: string | null;
    phone?: string | null;
  };
};

export type NationalPosition = {
  id: number;
  group: NationalGroupKey;
  name: string;
  slug: string;
  description: string | null;
  role_id: number | null;
  role_name?: string;
  role?: { id: number; name: string } | null;
  allows_multiple: boolean;
  rank: number | null;
  is_active: boolean;
  holders: NationalHolder[];
  current_holders_count: number;
};

export type NationalRegister = {
  groups: Record<string, string>;
  positions: NationalPosition[];
};

const payload = (res: any) => res?.data ?? res;

export const nationalBodyApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getNationalRegister: builder.query<NationalRegister, void>({
      query: () => ({ url: "/national-bodies/positions" }),
      transformResponse: (res: any): NationalRegister => {
        const d = payload(res) ?? {};
        return { groups: d.groups ?? {}, positions: d.positions ?? [] };
      },
      providesTags: [{ type: "NationalPositions" as const, id: "LIST" }],
    }),

    createNationalPosition: builder.mutation<any, Partial<NationalPosition>>({
      query: (body) => ({ url: "/national-bodies/positions", method: "POST", body }),
      invalidatesTags: [{ type: "NationalPositions" as const, id: "LIST" }],
    }),

    updateNationalPosition: builder.mutation<any, { id: number } & Partial<NationalPosition>>({
      // POST rather than PUT — the host blocks the other verbs on some routes,
      // and the API accepts both here.
      query: ({ id, ...body }) => ({ url: `/national-bodies/positions/${id}`, method: "POST", body }),
      invalidatesTags: [{ type: "NationalPositions" as const, id: "LIST" }],
    }),

    deleteNationalPosition: builder.mutation<any, number>({
      query: (id) => ({ url: `/national-bodies/positions/${id}`, method: "DELETE" }),
      invalidatesTags: [{ type: "NationalPositions" as const, id: "LIST" }],
    }),

    assignNationalHolder: builder.mutation<
      any,
      { positionId: number; member_id?: string; membership_no?: string; title?: string; started_at?: string }
    >({
      query: ({ positionId, ...body }) => ({
        url: `/national-bodies/positions/${positionId}/holders`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "NationalPositions" as const, id: "LIST" }],
    }),

    /**
     * Correct a holder's dates. The dates decide whether they are serving, so
     * the API moves the role with them — clearing the end date reinstates, and
     * setting one retires.
     */
    updateNationalHolder: builder.mutation<
      any,
      { holderId: number; title?: string | null; started_at?: string | null; ended_at?: string | null }
    >({
      query: ({ holderId, ...body }) => ({
        url: `/national-bodies/holders/${holderId}`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "NationalPositions" as const, id: "LIST" }],
    }),

    endNationalHolder: builder.mutation<any, { holderId: number; ended_at?: string }>({
      query: ({ holderId, ...body }) => ({
        url: `/national-bodies/holders/${holderId}/end`,
        method: "POST",
        body,
      }),
      invalidatesTags: [{ type: "NationalPositions" as const, id: "LIST" }],
    }),

    removeNationalHolder: builder.mutation<any, number>({
      query: (holderId) => ({ url: `/national-bodies/holders/${holderId}`, method: "DELETE" }),
      invalidatesTags: [{ type: "NationalPositions" as const, id: "LIST" }],
    }),
  }),
});

export const {
  useGetNationalRegisterQuery,
  useCreateNationalPositionMutation,
  useUpdateNationalPositionMutation,
  useDeleteNationalPositionMutation,
  useAssignNationalHolderMutation,
  useUpdateNationalHolderMutation,
  useEndNationalHolderMutation,
  useRemoveNationalHolderMutation,
} = nationalBodyApi;
